/**
 * WordPress Figmawya Courses API — enrollment + section quiz pass state (authoritative).
 */

const BASE_URL =
    (typeof process !== "undefined" && process.env.NEXT_PUBLIC_BASE_URL
        ? process.env.NEXT_PUBLIC_BASE_URL
        : "") + "api/figmawya/v1";

const getAuthHeaders = () => {
    const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
};

/**
 * @param {string|number} courseId
 * @param {number[]} quizIds - All section quiz IDs for this course page
 * @returns {Promise<{
 *   courseId: number,
 *   requiresEnrollment: boolean,
 *   enrollmentAccess: string,
 *   canViewContent: boolean,
 *   enrollment: object | null,
 *   quizPasses: Record<string, { passed: boolean, source?: string }>
 * } | null>}
 */
export async function fetchCourseLearnerState(courseId, quizIds) {
    if (typeof window === "undefined") return null;
    const ids = (quizIds || []).filter((n) => typeof n === "number" && n > 0);
    const qs = ids.length ? `?${new URLSearchParams({ quizIds: ids.join(",") }).toString()}` : "";

    try {
        const res = await fetch(`${BASE_URL}/courses/${encodeURIComponent(String(courseId))}/learner-state${qs}`, {
            method: "GET",
            headers: getAuthHeaders(),
            cache: "no-store",
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            console.warn("[course learner-state]", res.status, err);
            return null;
        }
        return await res.json();
    } catch (e) {
        console.warn("[course learner-state]", e);
        return null;
    }
}

/**
 * Count of learners with accepted enrollment for this course (WordPress plugin).
 * @param {string|number} courseId
 * @returns {Promise<number | null>}
 */
export async function fetchCourseAcceptedSubscriberCount(courseId) {
    if (courseId == null || courseId === "") return null;
    try {
        const res = await fetch(
            `${BASE_URL}/courses/${encodeURIComponent(String(courseId))}/accepted-count`,
            {
                method: "GET",
                headers: { "Content-Type": "application/json" },
                cache: "no-store",
            }
        );
        if (!res.ok) return null;
        const data = await res.json().catch(() => ({}));
        return typeof data.acceptedCount === "number" ? data.acceptedCount : null;
    } catch {
        return null;
    }
}

/**
 * Live count of all join requests for this course. Fetched client-side so the number
 * stays current even though the course page is ISR-cached (revalidate = 3600).
 * @param {string|number} courseId
 * @returns {Promise<number | null>}
 */
export async function fetchCourseRequestsCount(courseId) {
    if (courseId == null || courseId === "") return null;
    try {
        const res = await fetch(
            `${BASE_URL}/courses/${encodeURIComponent(String(courseId))}/requests-count`,
            {
                method: "GET",
                headers: { "Content-Type": "application/json" },
                cache: "no-store",
            }
        );
        if (!res.ok) return null;
        const data = await res.json().catch(() => ({}));
        return typeof data.requestsCount === "number" ? data.requestsCount : null;
    } catch {
        return null;
    }
}

/**
 * @param {string|number} courseId
 * @param {string} [motivation] optional; may be empty
 */
export async function postCourseApply(courseId, motivation) {
    const m = motivation != null ? String(motivation).trim() : "";
    const body = m.length > 0 ? { motivation: m } : {};
    const res = await fetch(`${BASE_URL}/courses/${encodeURIComponent(String(courseId))}/apply`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        let msg = data.error || data.message || `Apply failed (${res.status})`;
        // Stale WordPress plugin still requiring motivation length — tell user to deploy Courses API ≥ 1.1.1
        if (typeof msg === "string" && /motivation/i.test(msg) && /10|character/i.test(msg)) {
            msg =
                "تعذر إرسال الطلب: نسخة الإضافة على السيرفر قديمة. حدّث ملف إضافة Figmawya Courses API (الإصدار 1.1.1+) على ووردبرس.";
        }
        throw new Error(msg);
    }
    return data;
}
